import React from 'react';
import { Dropdown, Icon, Label, Pagination } from 'semantic-ui-react';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { PageSection } from 'common/styles/page';
import { SurveystructureComplianceRead } from 'store/generated/admin/category';
import { OrganizationRead } from 'store/generated/admin/organizations';

interface CategoryTableProps {
    categories: SurveystructureComplianceRead[];
    currentPage: number;
    onPageChange: (page: number) => void;
    onNavigate: (path: string) => void;
    onArchiveToggle: (category: SurveystructureComplianceRead) => void;
    organizationId?: number;
    organizations?: OrganizationRead[];
}

const PAGE_SIZE = 9;

const priorityOptions = [
    { key: 1, text: 'Primary', value: 1 },
    { key: 2, text: 'Custom', value: 2 },
    { key: 3, text: 'nuLogic', value: 3 },
];

const CategoryList: React.FC<CategoryTableProps> = ({
    categories,
    currentPage,
    onPageChange,
    onNavigate,
    onArchiveToggle,
    organizationId,
    organizations = [],
}) => {
    const pagedCategories = categories.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    return (
        <>
            <Table>
                <TableHeader>
                    <TableRow>
                        <HeaderCell width={4}>Name</HeaderCell>
                        {!organizationId && <HeaderCell width={3}>Organization</HeaderCell>}
                        <HeaderCell width={2}>Type</HeaderCell>
                        <HeaderCell width={2}>Visibility</HeaderCell>
                        <HeaderCell width={3}>Survey Prompt</HeaderCell>
                        <HeaderCell width={1} justify="center">Actions</HeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {pagedCategories.map(cat => (
                        <TableRow
                            key={cat.id}
                            $selectable
                            onClick={() => onNavigate(`/settings/admin/categories/${cat.id}/edit${organizationId ? '?orgId=' + organizationId : ''}`)}
                        >
                            <TableCell $firstcol>{cat.name}</TableCell>
                            {!organizationId && (
                                <TableCell>{organizations.find(org => org.id === cat.organization)?.name || '—'}</TableCell>
                            )}
                            <TableCell>
                                <Label color={cat.priority === 1 ? 'blue' : cat.priority === 2 ? 'yellow' : 'grey'}>
                                    {priorityOptions.find(opt => opt.value === cat.priority)?.text}
                                </Label>
                            </TableCell>

                            <TableCell>
                                <Label color={cat.default_visibility === 'public' ? 'green' : 'blue'}>
                                    {cat.default_visibility === 'public' ? 'Public' : 'Private'}
                                </Label>
                            </TableCell>
                            <TableCell>
                                <Label>
                                    <Icon
                                        name={
                                            cat.default_prompt === 'never_ask'
                                                ? 'times circle'
                                                : cat.default_prompt === 'always_ask'
                                                    ? 'check circle'
                                                    : 'question circle'
                                        }
                                        color={
                                            cat.default_prompt === 'never_ask'
                                                ? 'red'
                                                : cat.default_prompt === 'always_ask'
                                                    ? 'green'
                                                    : 'blue'
                                        }
                                    />
                                    {cat.default_prompt === 'never_ask' && 'Never ask'}
                                    {cat.default_prompt === 'always_ask' && 'Always ask'}
                                    {cat.default_prompt === 'ask_if_not_found' && 'Ask if not found'}
                                </Label>
                            </TableCell>
                            <TableCell justify="center">
                                <Dropdown
                                    button
                                    icon="ellipsis vertical"
                                    floating
                                    className="icon"
                                    direction="left"
                                    style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            icon="edit"
                                            text="Edit"
                                            onClick={() => onNavigate(`/settings/admin/categories/${cat.id}/edit${organizationId ? '?orgId=' + organizationId : ''}`)}
                                        />
                                        <Dropdown.Item
                                            icon="archive"
                                            text={cat.status === 2 ? 'Unarchive' : 'Archive'}
                                            onClick={() => onArchiveToggle(cat)}
                                        />
                                    </Dropdown.Menu>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <PageSection display="flex" justifyContent="end">
                <Pagination
                    activePage={currentPage}
                    totalPages={Math.ceil(categories.length / PAGE_SIZE)}
                    onPageChange={(_, data) => onPageChange(data.activePage as number)}
                    firstItem={null}
                    lastItem={null}
                    prevItem={{ content: '‹', icon: true }}
                    nextItem={{ content: '›', icon: true }}
                />
            </PageSection>
        </>
    );
};

export default CategoryList;
